import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { API_BASE_URL } from "../config/api";
import {
  LiqIntelDelinquencyTable,
  type DelinquencyRow,
} from "../components/LiqIntelDelinquencyTable";
import "./LiqIntelReport.css";

type DelinquencyPayload = {
  cert: string;
  source: string;
  section: string;
  rows: DelinquencyRow[];
  scrapedAt: string;
};

async function fetchDelinquencyScrape(cert: string): Promise<DelinquencyPayload> {
  const url = `${API_BASE_URL}/api/bank-reg-data/delinquency`;
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cert }),
  });
  const data: unknown = await r.json().catch(() => ({}));
  if (!r.ok) {
    const errMsg =
      typeof data === "object" &&
      data !== null &&
      "error" in data &&
      typeof (data as { error: unknown }).error === "string"
        ? (data as { error: string }).error
        : r.statusText || "Scrape failed";
    throw new Error(errMsg);
  }
  const payload = data as DelinquencyPayload;
  if (!Array.isArray(payload.rows)) {
    throw new Error("Invalid response: missing rows");
  }
  return payload;
}

const LiqIntelDelinquency: React.FC = () => {
  const [searchParams] = useSearchParams();
  const companyParam = searchParams.get("company")?.trim() ?? "";
  const certParam = searchParams.get("cert")?.trim() ?? "";
  const certDigits = certParam.replace(/\D/g, "").slice(0, 10);
  const hasValidCert = /^\d{1,10}$/.test(certDigits);

  const [rows, setRows] = useState<DelinquencyRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(hasValidCert && companyParam.length > 0);

  useEffect(() => {
    const titleBase =
      companyParam.length > 0 ? companyParam : hasValidCert ? `CERT ${certDigits}` : "Delinquency";
    document.title = `${titleBase} · Loans & Delinquency`;
  }, [companyParam, certDigits, hasValidCert]);

  useEffect(() => {
    if (!companyParam) {
      setLoading(false);
      setLoadError("Missing company query parameter.");
      return;
    }
    if (!hasValidCert) {
      setLoading(false);
      setLoadError(
        "Missing or invalid CERT. Add cert to the URL (e.g. &cert=628) or include CERT in the company field before opening this tab."
      );
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRows(null);

    fetchDelinquencyScrape(certDigits)
      .then((payload) => {
        if (!cancelled) {
          setRows(payload.rows);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : "Scrape failed");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyParam, certDigits, hasValidCert]);

  return (
    <div className="liq-report-page">
      <main className="liq-report-main">
        <div className="liq-report-topline">
          <div className="liq-report-brand">
            <img
              src="https://app.soteriamarket.com/banner3.jpeg"
              alt="Soteria logo"
              className="liq-report-logo"
            />
            <div className="liq-report-divider" />
            <span>Liquidity Intelligence</span>
          </div>
          <div className="liq-report-ai">
            <span className="liq-report-ai-label">Loans &amp; Delinquency</span>
          </div>
        </div>

        <header className="liq-report-hero">
          <div className="liq-report-hero-head">
            <AccountBalanceOutlinedIcon
              className="liq-report-hero-title-icon"
              aria-hidden
            />
            <div className="liq-report-hero-title-row">
              <h1>{companyParam || `CERT ${certDigits}`}</h1>
            </div>
            {hasValidCert ? (
              <p className="liq-report-hero-date" style={{ marginTop: "0.35rem" }}>
                CERT {certDigits}
              </p>
            ) : null}
          </div>
        </header>

        {loading ? (
          <div className="liq-report-loading-panel" role="status" aria-live="polite" style={{ marginTop: "2rem" }}>
            <CircularProgress size={56} thickness={4} />
            <p className="liq-report-loading-text">
              Loading delinquency data for <strong>{companyParam}</strong>…
            </p>
          </div>
        ) : loadError ? (
          <p className="liq-report-loading-text" style={{ color: "#b91c1c", marginTop: "1rem" }}>
            {loadError}
          </p>
        ) : rows && rows.length > 0 ? (
          <LiqIntelDelinquencyTable rows={rows} />
        ) : (
          <p className="liq-report-loading-text" style={{ marginTop: "1rem" }}>
            No delinquency rows were returned for this institution.
          </p>
        )}
      </main>
    </div>
  );
};

export default LiqIntelDelinquency;
